import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const DEACTIVATE_DELAY = 250;

class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];
  #abortController = new AbortController();
  #state = { activeItem: null };

  connectedCallback() {
    super.connectedCallback();
    onDocumentLoaded(this.#preloadImages);
    document.addEventListener('click', this.#handleDocumentClick, { signal: this.#abortController.signal });
    document.addEventListener('keydown', this.#handleKeydown, { signal: this.#abortController.signal });
    this.addEventListener('mouseleave', this.#handleMouseLeave, { signal: this.#abortController.signal });
  }

  disconnectedCallback() { super.disconnectedCallback(); this.#abortController.abort(); }
  get animationDelay() { const value = this.dataset.animationDelay; return value ? parseInt(value, 10) : 0; }
  get overflowMenu() { return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]') ?? null; }

  toggle = (event) => {
    this.#debouncedDeactivate.cancel();
    if (!(event.target instanceof Element)) return;
    const item = findMenuItem(event.target);
    if (!item) return;
    const submenu = findSubmenu(item);
    const isOverflowTrigger = event.target.closest('[slot="more"]') !== null;
    if (!submenu && !isOverflowTrigger) return;
    event.preventDefault(); event.stopPropagation();
    if (item === this.#state.activeItem) { this.#deactivate(item); return; }
    this.#activate(item, event.target);
  };

  #activate = (item, eventTarget) => {
    this.dispatchEvent(new MegaMenuHoverEvent());
    this.removeAttribute('data-animating');
    const isDefaultSlot = eventTarget?.closest('[slot="more"]') === null;
    this.dataset.overflowExpanded = (!isDefaultSlot).toString();
    const previouslyActiveItem = this.#state.activeItem;
    if (previouslyActiveItem) previouslyActiveItem.ariaExpanded = 'false';
    this.#state.activeItem = item;
    this.ariaExpanded = 'true'; item.ariaExpanded = 'true';
    let submenu = findSubmenu(item);
    const overflowMenuHeight = this.overflowMenu?.offsetHeight ?? 0;
    if (!submenu && !isDefaultSlot) submenu = this.overflowMenu;
    const submenuHeight = submenu ? Math.max(submenu.offsetHeight, overflowMenuHeight) : 0;
    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  };

  activate = () => {};
  deactivate = () => {};

  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item !== this.#state.activeItem) return;
    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';
    this.#state.activeItem = null; this.ariaExpanded = 'false'; item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');
    setTimeout(() => item.removeAttribute('data-animating'), Math.max(0, this.animationDelay - 150));
  };

  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);
  #handleMouseLeave = () => { if (this.#state.activeItem) this.#debouncedDeactivate(); };
  #handleDocumentClick = (event) => { if (!this.#state.activeItem) return; const path = event.composedPath?.() ?? []; if (!path.includes(this)) this.#deactivate(); };
  #handleKeydown = (event) => { if (event.key !== 'Escape' || !this.#state.activeItem) return; const activeItem = this.#state.activeItem; this.#deactivate(activeItem); activeItem.focus(); };
  #preloadImages = () => { this.querySelectorAll('img[loading="lazy"]')?.forEach((image) => image.removeAttribute('loading')); };
}

if (!customElements.get('header-menu')) customElements.define('header-menu', HeaderMenu);

function findMenuItem(element) {
  if (!(element instanceof Element)) return null;
  if (element.matches('[ref="menuitem"]')) return element;
  const moreItem = element.closest('[slot="more"]');
  if (moreItem) { const overflowItem = moreItem.parentElement?.querySelector('[slot="overflow"]'); return overflowItem?.querySelector('[ref="menuitem"]') ?? null; }
  const listItem = element.closest('.menu-list__list-item');
  return listItem?.querySelector('[ref="menuitem"]') ?? null;
}
function findSubmenu(element) { const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]'); return submenu instanceof HTMLElement ? submenu : null; }
